import {
  Configuration,
  Constants,
  logger,
  RequestUtils,
} from "@siemensplm/common_utils";
import CapabilityImageMap from "../context/CapabilityImagesMap.json";
import { capabilityType } from "src/types/CommonTypes";

/**
 * Compares a current version string against a minimum required version string
 * to determine whether the capability is supported or not.
 *
 * Version strings are expected to be dot-separated numeric segments (e.g. `"2506.0.0"`).
 * The comparison is performed segment-by-segment from left to right.
 * @param currentVersion - The version string of the currently installed component.
 * @param minimumRequiredVersion - The minimum version string that must be satisfied.
 * @returns `true` if `currentVersion` is greater than or equal to `minimumRequiredVersion`,
 *          `false` otherwise.
 */
export function compareVersions(
  currentVersion: unknown,
  minimumRequiredVersion: unknown
): boolean {
  const minString =
    typeof minimumRequiredVersion === "string"
      ? minimumRequiredVersion.trim()
      : "";
  if (!minString) return true;

  const currentString =
    typeof currentVersion === "string" ? currentVersion.trim() : "";
  if (!currentString) return false;

  // If minVersion is default, then all versions support the capability
  if (minString === Constants.defaultTcVersion) return true;

  // If TcVersion is default, then minVersion needs to be default i.e., all versions before 2506
  if (currentString === Constants.defaultTcVersion) {
    const minParts = minString.split(".");
    if (minParts.length > 0) {
      const minMajor = Number.parseInt(minParts[0], 10);
      if (minMajor < Constants.TcVersion2506) return true;
    }
    return false;
  }

  // Else just parse them and compare the versions by splitting using "."
  const minParts = minString.split(".");
  const currentParts = currentString.split(".");

  if (
    minParts.length === 0 ||
    currentParts.length === 0 ||
    currentParts.length < minParts.length
  )
    return false;

  for (let i = 0; i < minParts.length; i++) {
    const minPart = Number.parseInt(minParts[i], 10);
    const currentPart = Number.parseInt(currentParts[i], 10);

    if (currentPart > minPart) {
      return true;
    } else if (currentPart < minPart) {
      return false;
    }
  }

  return true;
}

export async function getSiteCapabilities(
  teamsContext: any,
  tcTeamsContext: any
) {
  logger.logInformation("Fetching site capabilities...");

  try {
    const response: any = await RequestUtils.callTcTeamsApi(
      "capability",
      tcTeamsContext.teamcenter.session // Pass the session for authenticated calls
    );

    if (response.error) {
      logger.logError(
        `Error fetching site capabilities: ${JSON.stringify(response.error)}`
      );
      return [];
    } else if (response.data) {
      if (Array.isArray(response.data) && response.data.length > 0) {
        logger.logInformation("Site capabilities loaded successfully");
        const cacheConfiguration = Configuration.getValueFromLocalStorage(
          Constants.cacheConfiguration
        );
        const currentTcVersion = cacheConfiguration.tcVersion ?? undefined;
        if (!currentTcVersion) {
          logger.logWarning("Current TcVersion is undefined.");
        }
        // Filter out Notifications for Teamcenter capability
        const capabilitiesList: capabilityType[] = response.data.filter(
          (capability: capabilityType) =>
            !(
              capability.capabilityName === "Notifications" &&
              capability.capabilityDomain === "Teamcenter"
            )
        );

        const versionCompatibleCapabilitiesList = capabilitiesList.filter(
          (capability: capabilityType) => {
            if (!capability.minTcVersion) return true;
            return compareVersions(currentTcVersion, capability.minTcVersion);
          }
        );

        if (
          versionCompatibleCapabilitiesList.length !== capabilitiesList.length
        ) {
          logger.logInformation(
            `Filtered capabilities by TC version (current=${String(
              currentTcVersion
            )}). ${capabilitiesList.length} -> ${
              versionCompatibleCapabilitiesList.length
            }`
          );
        }

        // Map capabilityImage from CapabilityImagesMap.json
        versionCompatibleCapabilitiesList.forEach(
          (capability: capabilityType) => {
            capability.capabilityImage =
              CapabilityImageMap.find(
                (cap) =>
                  cap.capabilityName === capability.capabilityName &&
                  cap.capabilityDomain === capability.capabilityDomain
              )?.capabilityImage || "";
          }
        );

        return versionCompatibleCapabilitiesList;
      } else {
        logger.logWarning("Site capabilities loaded but empty");
        return []; // Mark as loaded even if empty to prevent retry
      }
    } else {
      logger.logWarning("Site capabilities loaded but no data");
      return []; // Mark as loaded to prevent retry
    }
  } catch (error) {
    logger.logError(
      `Exception in getSiteCapabilities: ${JSON.stringify(error)}`
    );
    return []; // Mark as loaded to prevent retry on error
  }
}
